import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react'
import Stores from 'Stores'
import lessonStudentStore from '../LessonStudent/lessonStudentStore'
import { LessonContext } from '../BookChapter/LessonContext'
import Loader from 'components/UI/Loader/Loader'
import Exercise from '../Common/Exercise/Exercise'
import LessonToolbar from '../LessonStudent/LessonToolbar'
import BookPaper from '../Common/BookPaper/BookPaper'
import LessonHomeworkHeader from '../Common/LessonHomeworkHeader/LessonHomeworkHeader'
import st from '../LessonStudent/LessonStudent.module.scss'
import noExercisesImg from 'components/UI/illustrations/streamline-work-from-home.svg'
import LessonStudentStore from '../LessonStudent/lessonStudentStore'
import WithoutExercises from '../Common/WithoutExercises/WithoutExercises'
import { ReactComponent as SubmitReview } from 'components/UI/icons/review_icon.svg'
import { superAxiosRequest } from 'axiosApi'
import ShowMark from '../ShowMark/ShowMark'
import swal from 'sweetalert'
import { localisationMaster } from 'Localisation/Localisation'
import ReviewInfoBlock from './ReviewInfoBlock/ReviewInfoBlock'
import LessonDiscussion from '../LessonDiscussion/LessonDiscussion'
import LessonLogs from '../LessonLogs/LessonLogs'
import useLocalization from 'hooks/useLocalization'
import { useAIChat } from 'hooks/useAIChat'
import AIButton from 'components/UI/AIButton/AIButton'
import AIChat from 'components/ContentBox/AIChat/AIChat'

function HomeworkStudent() {
  const { hash } = useParams()

  const { toggleChat, isAIChatOpen } = useAIChat(hash, 'homework')

  const { lesson, errorStatus, clearLesson } = lessonStudentStore

  const context = { mode: 'student' }

  const wsRef = useRef()

  const [isSubmit, setIsSubmit] = useState(false)

  const [currentRef, setCurrentRef] = useState(null)

  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'exercises',
    dict: {
      homework: 'Homework',
      save_answer: 'Save Answer',
      add_to_favorites: 'Add to Favorites',
      action_canceled: 'Action canceled',

      lesson_discussion: 'Lesson Discussion',
      theres_is_no_discussion_yet: "There's no discussion yet",
      to_start_comment: 'To start a discussion, leave a comment',
      send: 'Send',

      send_lesson_for_review: 'Send Lesson For Review',
      hm_not_found: 'Homework not found',
      alert_submit_hm:
        'Are you sure you want to submit your homework for review',
      no_homework_yet: 'No homework yet',
      no_hm_descr:
        'The teacher has not yet assigned exercises for homework. Stay tuned',
      leave_a_comment: 'Leave a Comment',
      teacher_sent_for_revision: 'Teacher Sent For Revision',

      homework_passed: 'Homework Passed',
      teacher_confirmed_the_lesson: 'Teacher Confirmed The Lesson',
      failed: 'Failed',
      logs_not_found: 'Logs not found',
      discussion: 'Discussion',
      homework_checking: 'Homework has been sent to the teacher for checking',
      homework_is_checked: 'Homework is checked by the teacher',
      sent_for_revision: 'and sent for revision',
      Homework_has_been_checked: 'Homework has been checked by the teacher',
      and_confirmed: 'and confirmed',
      error: 'Error',
      didnt_answer_task: 'You didn’t answer all the tasks in the exercise',
      dislike: 'Dislike',
      dont_forget: "Don't forget to save your answer",
    },
  })

  const scrollToBlock = () => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    lessonStudentStore.fetchHomework(hash)

    const wsUrl = `${Stores.baseLink().replace('https', 'wss')}/ws/ebooks/homework/${hash}/?token=${localStorage.getItem('access')}`
    wsRef.current = new WebSocket(wsUrl)
    wsRef.current.onmessage = ({ data }) => {
      const { type, message } = JSON.parse(data)
      if (type === 'ebooks.updated_homework_exercises') {
        LessonStudentStore.setLessonExercises(message.exercises)
        LessonStudentStore.setLessonAssignExercises(message.assign_exercises)
      }
    }

    return () => {
      clearLesson()
    }
  }, [clearLesson, hash])

  const submitReview = () => {
    swal({
      text: `${translate.alert_submit_hm}?`,
      icon: 'warning',
      buttons: [
        localisationMaster('button_2', 'alert'),
        localisationMaster('button_1', 'alert'),
      ],
      dangerMode: true,
    }).then((yes) => {
      if (yes) {
        setIsSubmit(true)
        superAxiosRequest(
          {
            method: 'post',
            link: 'ebooks/student_lesson_reviews',
          },
          {
            homework_hash: hash,
          },
        )
      } else {
        setIsSubmit(false)
        swal(translate.action_canceled)
      }
    })
  }

  return lesson ? (
    <div className={st.lessonStudent}>
      <LessonToolbar
        lessonLink={`/ebooks/${Stores.isClient ? 'my-' : ''}lesson/${lesson.init_lesson_uuid}`}
      />
      <div className={st.bookContainer}>
        <BookPaper>
          <div className={st.bookColumn}>
            <LessonHomeworkHeader
              title={translate.homework}
              createdAt={lesson.created_at}
              ebookName={lesson.chapter_ebook_name}
              chapterName={lesson.chapter_name}
            />
            {lesson?.mark && (
              <ShowMark
                translate={translate}
                mark={Number(lesson?.mark)}
                type={lesson?.company_mark_type}
                scroll={scrollToBlock}
              />
            )}
            {!lesson?.mark && !isSubmit && lesson?.review === false && (
              <ReviewInfoBlock
                translate={translate}
                scrollToBlock={scrollToBlock}
              />
            )}
            {lesson.exercises.length > 0 ? (
              <LessonContext.Provider value={context}>
                {lesson.exercises.map((ex, index) => (
                  <div key={ex.id}>
                    <Exercise
                      number={`1.${index + 1}`}
                      ex={ex}
                      translate={translate}
                    />
                  </div>
                ))}
              </LessonContext.Provider>
            ) : (
              <WithoutExercises
                text1={`${translate.no_homework_yet} :(`}
                text2={`${translate.no_hm_descr} :)`}
              />
            )}
          </div>
        </BookPaper>
        {lesson.exercises.length > 0 && !lesson?.review && !isSubmit && (
          <div className={st.review}>
            <div className={st.reviewBtn} onClick={submitReview}>
              <SubmitReview />
              <span>{translate.send_lesson_for_review}</span>
            </div>
          </div>
        )}
        <LessonDiscussion
          translate={translate}
          context={'student'}
          hash={hash}
          isHomework={true}
          setRef={setCurrentRef}
        />
        <LessonLogs
          translate={translate}
          context={'student'}
          isHomeWork={true}
          hash={hash}
        />
      </div>
      {!isAIChatOpen && (
        <div className={st.aiButton}>
          <AIButton onClick={toggleChat} />
        </div>
      )}
      {isAIChatOpen && (
        <div className={st.aiChat}>
          <AIChat toggleChat={toggleChat} />
        </div>
      )}
    </div>
  ) : (
    <div className={st.lessonStudent}>
      <LessonToolbar />
      <div className={st.bookContainer}>
        <BookPaper>
          {errorStatus === 404 ? (
            <div className={st.noExercises}>
              <div>
                <img src={noExercisesImg} alt='' />
              </div>
              <p>404</p>
              <p>&nbsp;</p>
              <p>{translate.hm_not_found}</p>
            </div>
          ) : (
            <Loader />
          )}
        </BookPaper>
      </div>
    </div>
  )
}

export default observer(HomeworkStudent)
